import React, { useCallback, useEffect, useId, useState } from 'react';

import { normalizeHexColorInput, preserveHexColorAlpha } from '../hex-color';

type HexColorInput = {
  text: string;
  invalid: boolean;
  errorId: string;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSwatchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setColor: (value: string) => void;
};

/** Complete colors commit immediately; incomplete/invalid edits stay local (no blur-time sibling-field race). */
export function useHexColorInput(value: string | null | undefined, onChange: (value: string) => void): HexColorInput {
  const normalizedValue = value ?? '';
  const [text, setText] = useState(normalizedValue);
  const errorId = useId();

  useEffect(() => {
    // A parent echo must not insert # into an unprefixed draft or move the caret while typing.
    setText((current) => (normalizeHexColorInput(current) === normalizedValue ? current : normalizedValue));
  }, [normalizedValue]);

  const setColor = useCallback(
    (input: string) => {
      setText(input);
      const hex = normalizeHexColorInput(input);
      if (hex !== null) onChange(hex);
    },
    [onChange]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setColor(event.currentTarget.value),
    [setColor]
  );

  const handleSwatchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setColor(preserveHexColorAlpha(event.currentTarget.value, normalizedValue)),
    [setColor, normalizedValue]
  );

  return { text, invalid: normalizeHexColorInput(text) === null, errorId, handleChange, handleSwatchChange, setColor };
}

export function HexColorError({ id }: { id: string }): JSX.Element {
  return (
    <span
      id={id}
      role='alert'
      style={{ display: 'block', marginTop: 4, color: 'var(--dcm-cms-color-error, var(--puck-color-red-06, #ad0000))' }}
    >
      Enter 3, 4, 6 or 8 hex digits (0–9, A–F), optionally starting with #. Invalid text is not saved.
    </span>
  );
}
